import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';

import { APP_URL_CATALOG, APP_URL_CHAT, APP_URL_CHAT_HISTORY, APP_URL_PROFILE } from '../../../const';
import LoggerService from '../../../service/LoggerService';
import {
    getUser,
    getUserLimits,
    getUserPaymentPopupState,
    getUserPlan,
    getUserUnreadMessages,
    logOut
} from '../../../store/slices/authSlice';
import { getActiveChatLoadingState, getActiveChatState, startNewChat } from '../../../store/slices/chatsSlice';
import { getUserMenuState, toggleUserMenu } from '../../../store/slices/globalSlice';
import { setShowPaymentModal } from '../../../store/slices/modalsSlice';
import { gaEvents } from '../../../utils/analytics';
import Spinner from '../../common/Spinner';
import SubscriptionBanner from '../../common/SubscriptionBanner';
import CrossIcon from '../../icons/CrossIcon';
import CatalogIcon from '../../icons/UserMenu/CatalogIcon';
import ChatHistory from '../../icons/UserMenu/ChatHistory';
import LogoutIcon from '../../icons/UserMenu/LogoutIocn';
import NewChatIcon from '../../icons/UserMenu/NewChatIcon';
import UserMenuIcon from '../../icons/UserMenu/UserMenuIcon';
import ItemList from './ItemList/ItemList';
import styles from './styles.module.scss';

const UserMenu = () => {
    const dispatch = useDispatch();
    const isShow = useSelector(getUserMenuState);
    const { messages } = useSelector(getActiveChatState);
    const navigate = useNavigate();
    const user = useSelector(getUser);
    const isUser = user?.id;
    const isNewChatLoading = useSelector(getActiveChatLoadingState);
    const unreadMessages = useSelector(getUserUnreadMessages);
    const timeoutRef = useRef(null);
    const { total, used } = useSelector(getUserLimits);
    const { isTrial } = useSelector(getUserPlan);
    const isChat = useMatch(`/${APP_URL_CHAT}/*`);
    const { t } = useTranslation();
    const paymentPopup = useSelector(getUserPaymentPopupState);

    const config = useMemo(() => {
        return [
            {
                title: 'menu.catalog',
                pathTo: `/${APP_URL_CATALOG}`,
                onClick: () => {
                    dispatch(toggleUserMenu(false));
                },
                Icon: CatalogIcon,
                isNeedUser: false
            },
            {
                title: isUser ? 'menu.profile' : 'menu.profileCreate',
                pathTo: `/${APP_URL_PROFILE}`,
                onClick: () => {
                    dispatch(toggleUserMenu(false));
                },
                Icon: UserMenuIcon
            },
            {
                title: 'menu.newChat',
                pathTo: `/${APP_URL_CHAT}`,
                onClick: (e) => {
                    e.preventDefault();
                    if (isNewChatLoading) {
                        return;
                    }
                    if (total === used) {
                        dispatch(toggleUserMenu(false));
                        dispatch(setShowPaymentModal(true));
                        gaEvents.openPaymentPopup(paymentPopup);
                        return;
                    }
                    if (isChat && messages.length < 2) {
                        dispatch(toggleUserMenu(false));
                        return;
                    }
                    dispatch(startNewChat())
                        .then((result) => {
                            if (result?.meta?.requestStatus === 'fulfilled') {
                                dispatch(toggleUserMenu(false));
                                navigate(`/${APP_URL_CHAT}/${result?.payload?.advisor?.alias}/${result?.payload?.id}`);
                            }
                        })
                        .catch((error) => {
                            LoggerService.log(error);
                            dispatch(toggleUserMenu(false));
                        });
                },
                Icon: NewChatIcon
            },
            {
                title: 'menu.history',
                pathTo: `/${APP_URL_CHAT_HISTORY}`,
                onClick: () => {
                    dispatch(toggleUserMenu(false));
                },
                Icon: ChatHistory,
                unreadMessages
            }
        ];
    }, [dispatch, isUser, messages.length, navigate, isNewChatLoading, unreadMessages, total, used]);

    const [localShow, setLocalShow] = useState(isShow);

    const onClose = () => {
        setLocalShow(false);
        timeoutRef.current = setTimeout(() => {
            dispatch(toggleUserMenu(false));
        }, 300);
    };

    useEffect(() => {
        setLocalShow(isShow);
    }, [isShow]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isShow) {
            document.querySelector('html').style.overflowY = 'unset';
            return;
        }
        document.querySelector('html').style.overflowY = 'hidden';
    }, [isShow]);

    useEffect(() => {
        return () => {
            document.querySelector('html').style.overflowY = 'unset';
        };
    }, []);

    if (!isShow) {
        return null;
    }

    const userLogOut = () => {
        dispatch(logOut());
    };

    return (
        <motion.div className={styles.wrapper} initial={{ x: -600 }} animate={{ x: localShow ? 0 : -600 }} transition={{ duration: 0.2 }}>
            <div className={styles.overly} onClick={onClose} onKeyDown={onClose} role="presentation">
                {isNewChatLoading && (
                    <div className={styles.spinner}>
                        <Spinner width={100} height={100} />
                    </div>
                )}
            </div>
            <div className={classNames(styles.inner)}>
                <div className={styles.top}>
                    <span className={styles.title}>{t('menu.title')}</span>
                    <button className={styles.btn} onClick={onClose}>
                        <CrossIcon />
                    </button>
                </div>
                {isUser && !isTrial && <SubscriptionBanner inlineStyle={{ marginBottom: '12px', minHeight: 114 }} />}
                <ItemList data={config} />
                {isUser && (
                    <button onClick={userLogOut} className={styles.logout}>
                        <LogoutIcon />
                        <span>{t('buttons.logout')}</span>
                    </button>
                )}
            </div>
        </motion.div>
    );
};
export default UserMenu;
